import { useRef, useState, useEffect } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import api from "@/lib/api";
import { FileUp, UploadCloud, CheckCircle2, AlertTriangle, Loader2, PackagePlus, Store, X, History, FileDown } from "lucide-react";
import { toast } from "sonner";

const ACCENT = "#0f766e";
const money = (n) => `£${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OrderImport() {
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committed, setCommitted] = useState(null);
  const [history, setHistory] = useState([]);
  const [overrides, setOverrides] = useState({});
  const inputRef = useRef(null);

  const loadHistory = async () => {
    try { const { data } = await api.get("/order-import/history"); setHistory(data.imports || []); } catch { /* no-op */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const pick = (e) => { setFiles(Array.from(e.target.files || [])); setPreview(null); setCommitted(null); };

  const doPreview = async () => {
    if (!files.length) { toast.error("Choose one or more .txt order files"); return; }
    setBusy(true); setCommitted(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/order-import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`Parsed ${data.summary.orders} order(s) from ${data.summary.files} file(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); } finally { setBusy(false); }
  };

  const doCommit = async () => {
    const orders = (preview?.orders || []).filter((o) => !o.error && !o.duplicate).map((o) => ({ ...o, ship_to_override: overrides[o.order_no] || undefined }));
    if (!orders.length) { toast.error("Nothing to import"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/order-import/commit", { orders });
      setCommitted(data);
      toast.success(`Created ${data.created.length} sales order(s)${data.new_materials ? ` · ${data.new_materials} new materials` : ""}`);
      setPreview(null); setFiles([]);
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); } finally { setBusy(false); }
  };

  const s = preview?.summary;
  const importable = (preview?.orders || []).filter((o) => !o.error && !o.duplicate).length;
  const downloadErrors = () => {
    const rows = (preview?.orders || []).filter((o) => o.error);
    const csv = "file,error\n" + rows.map((o) => `"${o.file}","${(o.error || "").replace(/"/g, "'")}"`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "import-errors.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="order-import">
      <PageHeader title="Order File Import" subtitle="Upload M&S e-commerce order files (.txt). Each file becomes one UK sales order — Sold-To M&S account, Ship-To resolved from the store code, missing materials auto-created." icon={FileUp} accent={ACCENT} />

      <div className="mb-6 rounded-xl border border-dashed border-[#c9d2dc] bg-white p-6 text-center shadow-sm" data-testid="oi-dropzone">
        <UploadCloud className="mx-auto mb-2 text-[#0f766e]" size={28} />
        <input ref={inputRef} data-testid="oi-file-input" type="file" accept=".txt,text/plain" multiple onChange={pick} className="hidden" />
        <button data-testid="oi-choose" onClick={() => inputRef.current?.click()} className="rounded-md bg-[#0f766e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0b5f58]">Choose order files</button>
        <div className="mt-2 text-xs text-[#6a6d70]">{files.length ? `${files.length} file(s) selected` : "Tab-separated .txt files exported from the M&S ordering site"}</div>
        {files.length > 0 && (
          <div className="mx-auto mt-3 flex max-w-xl flex-wrap justify-center gap-2">
            {files.map((f) => (
              <span key={f.name} className="inline-flex items-center gap-1 rounded-full bg-[#eef1f5] px-2.5 py-1 text-[11px] text-[#32363a]">{f.name}
                <X size={11} className="cursor-pointer text-[#89919a] hover:text-red-500" onClick={() => setFiles((x) => x.filter((y) => y !== f))} /></span>
            ))}
          </div>
        )}
        <div className="mt-4">
          <button data-testid="oi-preview" onClick={doPreview} disabled={busy || !files.length} className="inline-flex items-center gap-1.5 rounded-md border border-[#0f766e] px-4 py-2 text-sm font-semibold text-[#0f766e] hover:bg-[#f0fdfa] disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />} Preview &amp; validate
          </button>
        </div>
      </div>

      {committed && (
        <div data-testid="oi-committed" className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-800"><CheckCircle2 size={16} /> Imported {committed.created.length} sales order(s){committed.new_materials ? ` · ${committed.new_materials} new materials created` : ""}{committed.skipped.length ? ` · ${committed.skipped.length} skipped (duplicates)` : ""}</div>
          <div className="flex flex-wrap gap-2">
            {committed.created.map((c) => (
              <a key={c.order_no} href={`/sales`} className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1 text-xs font-semibold text-[#0f766e] shadow-sm hover:underline">{c.order_no} · {c.store} · {c.lines} lines · {money(c.value)}</a>
            ))}
          </div>
        </div>
      )}

      {preview && (
        <div className="space-y-4" data-testid="oi-preview-panel">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[["Files", s.files], ["Orders", s.orders], ["Lines", s.lines], ["Total value", money(s.value)], ["New materials", s.new_materials]].map(([l, v]) => (
              <div key={l} className="rounded-xl border border-[#e5e9f0] bg-white p-3 text-center shadow-sm"><div className="text-lg font-bold text-[#32363a]">{v}</div><div className="text-[10px] uppercase text-[#89919a]">{l}</div></div>
            ))}
          </div>
          {(s.duplicates > 0 || s.unknown_stores > 0) && (
            <div className="flex flex-wrap gap-2 text-xs">
              {s.duplicates > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 font-semibold text-amber-700"><AlertTriangle size={12} /> {s.duplicates} already imported (skipped)</span>}
              {s.unknown_stores > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 font-semibold text-sky-700"><Store size={12} /> {s.unknown_stores} new store(s) will be created</span>}
            </div>
          )}
          <div className="overflow-hidden rounded-xl border border-[#e5e9f0] bg-white shadow-sm" data-testid="oi-preview-table">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wide text-[#6a6d70]">
                <tr className="border-b border-[#eef1f5]"><th className="px-3 py-2">M&amp;S Order</th><th className="px-3 py-2">Ship-To store</th><th className="px-3 py-2">Delivery</th><th className="px-3 py-2 text-right">Lines</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Value</th><th className="px-3 py-2 text-right">New mat.</th><th className="px-3 py-2">Status</th></tr>
              </thead>
              <tbody>
                {preview.orders.map((o, i) => (
                  <tr key={i} data-testid={`oi-row-${i}`} className="border-b border-[#f2f4f7] last:border-0">
                    {o.error ? (
                      <td colSpan={8} className="px-3 py-2 text-xs text-red-600"><AlertTriangle size={12} className="mr-1 inline" /> {o.file}: {o.error}</td>
                    ) : (
                      <>
                        <td className="px-3 py-2 font-mono font-semibold text-[#DB2777]">{o.order_no}</td>
                        <td className="px-3 py-2"><span className="font-semibold text-[#32363a]">{o.store_name}</span> <span className="text-[11px] text-[#89919a]">{o.store_code}</span>{!o.store_known && <span className="ml-1 rounded bg-sky-100 px-1 text-[9px] font-bold text-sky-700">NEW</span>}{!o.store_known && (<div><input data-testid={`oi-map-${i}`} value={overrides[o.order_no] || ""} onChange={(e) => setOverrides((m) => ({ ...m, [o.order_no]: e.target.value }))} placeholder="map to existing code" className="mt-1 w-40 rounded border border-[#c8cfda] px-1.5 py-0.5 text-[10px] outline-none focus:border-[#0f766e]" /></div>)}</td>
                        <td className="px-3 py-2 text-xs text-[#6a6d70]">{o.delivery_date || "—"}</td>
                        <td className="px-3 py-2 text-right">{o.lines.length}</td>
                        <td className="px-3 py-2 text-right">{o.total_qty}</td>
                        <td className="px-3 py-2 text-right font-semibold">{money(o.total_value)}</td>
                        <td className="px-3 py-2 text-right">{o.new_materials ? <span className="inline-flex items-center gap-0.5 text-amber-600"><PackagePlus size={11} />{o.new_materials}</span> : "—"}</td>
                        <td className="px-3 py-2">{o.duplicate ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">SKIP</span> : <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">READY</span>}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-end gap-3">
            <span className="text-xs text-[#6a6d70]">{importable} order(s) ready to create</span>
            <button data-testid="oi-commit" onClick={doCommit} disabled={busy || !importable} className="inline-flex items-center gap-1.5 rounded-md bg-[#0f766e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0b5f58] disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Confirm &amp; create sales orders
            </button>
          </div>
        </div>
      )}

      <div className="mt-8" data-testid="oi-history">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]"><History size={15} /> Import history</h3>
        {history.length === 0 ? (
          <div className="rounded-xl border border-[#e5e9f0] bg-white p-6 text-center text-sm text-[#89919a] shadow-sm">No imports yet — your successful, time-stamped imports will appear here.</div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-[#e5e9f0] bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wide text-[#6a6d70]">
                <tr className="border-b border-[#eef1f5]"><th className="px-3 py-2">Date &amp; time</th><th className="px-3 py-2">By</th><th className="px-3 py-2 text-right">Files</th><th className="px-3 py-2 text-right">Orders</th><th className="px-3 py-2 text-right">Value</th><th className="px-3 py-2 text-right">New mat.</th><th className="px-3 py-2 text-right">Skipped</th><th className="px-3 py-2">Result</th></tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} data-testid={`oi-hist-${h.id}`} className="border-b border-[#f2f4f7] align-top last:border-0">
                    <td className="whitespace-nowrap px-3 py-2 text-[#32363a]">{new Date(h.at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</td>
                    <td className="px-3 py-2 text-xs text-[#6a6d70]">{h.by_name || h.by}</td>
                    <td className="px-3 py-2 text-right">{h.files}</td>
                    <td className="px-3 py-2 text-right font-semibold">{h.orders_created}</td>
                    <td className="px-3 py-2 text-right font-semibold">{money(h.value)}</td>
                    <td className="px-3 py-2 text-right">{h.new_materials || "—"}</td>
                    <td className="px-3 py-2 text-right">{h.skipped || "—"}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><CheckCircle2 size={11} /> Success</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(h.created || []).slice(0, 6).map((c) => (
                          <a key={c.order_no} href="/sales" className="rounded bg-[#eef1f5] px-1.5 py-0.5 font-mono text-[10px] text-[#0f766e] hover:underline" title={`${c.customer_po_no} → ${c.store}`}>{c.order_no}</a>
                        ))}
                        {(h.created || []).length > 6 && <span className="text-[10px] text-[#89919a]">+{h.created.length - 6} more</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
